import React, { useState, useEffect } from "react";
import { FaEdit, FaTrash, FaSearch } from "react-icons/fa";
import api from "../../api"; // axios instance (token + interceptors)

const ExpenseSubCategoryPage = () => {
  const [form, setForm] = useState({ category_id: "", name: "", description: "" });
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editId, setEditId] = useState(null);
  const [msg, setMsg] = useState(null);
  const [search, setSearch] = useState("");

  const baseURL = "/expense/subcategory";

  // 🔹 Fetch Categories for Dropdown
  const fetchCategories = async () => {
    try {
      const res = await api.get("/expense/category/list");
      const data = res.data;
      setCategories(Array.isArray(data.data) ? data.data : []);
    } catch (err) {
      console.error("Error fetching categories:", err);
      setCategories([]);
    }
  };

  // 🔹 Fetch Subcategories
  const fetchSubcategories = async () => {
    try {
      const res = await api.get(`${baseURL}/list`);
      const data = res.data;
      setItems(Array.isArray(data.data) ? data.data : []);
    } catch (err) {
      console.error("Error fetching subcategories:", err);
      setItems([]);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchSubcategories();
  }, []);

  // 🔹 Handle Input Change
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // 🔹 Submit Form (Add / Update)
  const handleSubmit = async (e) => {
    e.preventDefault();
    setMsg(null);

    if (!form.category_id || !form.name.trim()) {
      setMsg({ type: "error", text: "Category and Subcategory name required" });
      return;
    }

    try {
      let res;
      if (editId) {
        res = await api.post(`${baseURL}/update`, { ...form, id: editId });
      } else {
        res = await api.post(`${baseURL}/add`, form);
      }

      const json = res.data;
      if (json?.status) {
        setMsg({
          type: "success",
          text: json.message || (editId ? "Updated successfully" : "Added successfully"),
        });
        setForm({ category_id: "", name: "", description: "" });
        setEditId(null);
        fetchSubcategories();
      } else {
        setMsg({ type: "error", text: json?.message || "Operation failed" });
      }
    } catch (err) {
      console.error("Error saving subcategory:", err);
      setMsg({
        type: "error",
        text: err.response?.data?.message || err.message || "Network error",
      });
    }
  };

  // 🔹 Edit Subcategory
  const handleEdit = (item) => {
    setForm({
      category_id: item.category_id || "",
      name: item.name || "",
      description: item.description || "",
    });
    setEditId(item.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // 🔹 Delete Subcategory
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this subcategory?")) return;
    try {
      const res = await api.post(`${baseURL}/delete`, { id });
      const json = res.data;
      setMsg({
        type: json?.status ? "success" : "error",
        text: json?.message || (json?.status ? "Deleted" : "Delete failed"),
      });
      fetchSubcategories();
    } catch (err) {
      setMsg({
        type: "error",
        text: err.response?.data?.message || err.message || "Network error",
      });
    }
  };

  // 🔹 Search filter
  const filteredItems = items.filter((item) =>
    item.name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={{ display: "flex", minHeight: "100vh" }}>
      <div style={{ flex: 1, backgroundColor: "#f4f6f8" }}>
        <main style={{ padding: "30px", maxWidth: "1000px", margin: "auto" }}>
          {/* 🔸 Add / Edit Section */}
          <section style={cardStyle}>
            <h2 style={titleStyle}>
              {editId ? "✏️ Edit Subcategory" : "➕ Add New Subcategory"}
            </h2>

            {msg && (
              <div
                style={{
                  marginBottom: 12,
                  color: msg.type === "error" ? "#cc0000" : "#166534",
                }}
              >
                {msg.text}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div style={formGrid}>
                <FormGroup label="Select Category">
                  <select
                    name="category_id"
                    value={form.category_id}
                    onChange={handleChange}
                    style={fullWidthInput}
                    required
                  >
                    <option value="">-- Select Category --</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </FormGroup>

                <FormGroup label="Subcategory Name">
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    style={fullWidthInput}
                    placeholder="Enter subcategory name"
                    required
                  />
                </FormGroup>

                <FormGroup label="Description (optional)">
                  <input
                    type="text"
                    name="description"
                    value={form.description}
                    onChange={handleChange}
                    style={fullWidthInput}
                    placeholder="Enter short note (optional)"
                  />
                </FormGroup>
              </div>

              <div style={{ textAlign: "right", marginTop: "20px" }}>
                <button type="submit" style={buttonStyle}>
                  {editId ? "Update Subcategory" : "Add Subcategory"}
                </button>
              </div>
            </form>
          </section>

          {/* 🔸 Search Bar */}
          <div
            style={{
              ...cardStyle,
              marginBottom: "20px",
              display: "flex",
              alignItems: "center",
            }}
          >
            <FaSearch style={{ marginRight: 10, color: "#0077cc" }} />
            <input
              type="text"
              placeholder="Search subcategory by name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ ...fullWidthInput, width: "100%" }}
            />
          </div>

          {/* 🔸 List Subcategories */}
          <section style={cardStyle}>
            <h2 style={titleStyle}>📋 All Subcategories</h2>

            {filteredItems.length === 0 ? (
              <p>No subcategories found.</p>
            ) : (
              <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {filteredItems.map((item, idx) => (
                  <li
                    key={item.id}
                    style={{
                      marginBottom: "15px",
                      padding: "15px",
                      borderRadius: "8px",
                      background: idx % 2 === 0 ? "#f9f9f9" : "#eef3f9",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div>
                      <strong style={{ fontSize: "16px", color: "#333" }}>
                        {item.name}
                      </strong>
                      <div style={{ color: "#777", fontSize: "13px" }}>
                        Category:{" "}
                        {
                          categories.find((cat) => cat.id === item.category_id)
                            ?.name
                        }{" "}
                        | {item.description || "No description"}
                      </div>
                    </div>

                    <div>
                      <FaEdit
                        onClick={() => handleEdit(item)}
                        style={{
                          color: "#0077cc",
                          cursor: "pointer",
                          marginRight: "15px",
                        }}
                      />
                      <FaTrash
                        onClick={() => handleDelete(item.id)}
                        style={{ color: "red", cursor: "pointer" }}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </main>
      </div>
    </div>
  );
};

// 🔹 Reusable FormGroup
const FormGroup = ({ label, children, style }) => (
  <div style={{ ...style }}>
    <label
      style={{
        marginBottom: "6px",
        display: "block",
        fontWeight: 500,
        color: "#444",
      }}
    >
      {label}
    </label>
    {children}
  </div>
);

// 🔹 Common Styles
const formGrid = {
  display: "grid",
  gridTemplateColumns: "1fr",
  gap: "20px",
};

const fullWidthInput = {
  width: "100%",
  padding: "10px",
  borderRadius: "6px",
  border: "1px solid #ccc",
  fontSize: "15px",
  boxSizing: "border-box",
};

const buttonStyle = {
  padding: "10px 25px",
  backgroundColor: "#0077cc",
  color: "#fff",
  border: "none",
  borderRadius: "6px",
  fontSize: "15px",
  cursor: "pointer",
};

const cardStyle = {
  background: "#ffffff",
  padding: "25px",
  borderRadius: "12px",
  boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
  marginBottom: "30px",
};

const titleStyle = {
  color: "#0077cc",
  marginBottom: "25px",
};

export default ExpenseSubCategoryPage;
